import React from 'react';
import {
  ExclamationTriangleIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  ClockIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { adjacencyBadgeText } from '../../utils/ipAdjacency';

/**
 * Per-IP evidence for a row recovered from the NON-INTERACTIVE Entra sign-in feed
 * (v1.147.0). Rendered inside the case IP Addresses table's expanded row.
 *
 * Why this is a panel and not a column. An interactive sign-in is a self-contained
 * story — a human presented credentials, from a place, on a device, and passed or
 * failed MFA. The analyst evaluates it by asking "does this look like my user logging
 * in?". A non-interactive row has no story: a machine presented a token, the user did
 * nothing at that moment, and one row looks exactly like the other 11,736. So a bare
 * list of IPs gives an analyst no way in, and the verdicts have to come from
 * RELATIONSHIPS the portal already computes.
 *
 * Order is deliberate — DEVICE FIRST. That is the pipeline team's primary rule
 * ("judge an IP by the device on its records, not by novelty or absence from
 * history"), and it is the rule that separated confirmed `45.88.220.114` from
 * discarded `70.170.144.13` on the reference case. It also promotes all three of the
 * IPs this feature recovers.
 *
 * ⚠️ Three things deliberately NOT rendered, each because it looks like evidence and
 * is not:
 *   - a shared SessionId (one spans 45 IP/client groups including legitimate ones —
 *     a long-lived mailbox session, not a session in the ordinary sense);
 *   - a browser family on its own (the legitimate office IP has 55 Firefox sign-ins);
 *   - a scripted-client UA as a VERDICT. It is a caution only: an estate-wide sweep
 *     returned the incident responder's own tooling, and on the reference case the
 *     victim's own office IP carries `node-fetch/1.0`.
 */

const SCRIPTED_UA = /(python|curl|requests|go-http|axios|postman|node-fetch|wget|okhttp|libwww|httpie)/i;

/** UA families that indicate automation. A caution, never a verdict — see docblock. */
export function scriptedClients(userAgents = []) {
  return (userAgents || []).filter((ua) => SCRIPTED_UA.test(String(ua || '')));
}

const fmtDate = (iso) => {
  if (!iso) return '—';
  const s = String(iso);
  return s.length >= 10 ? s.slice(0, 10) : s;
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const s = String(iso);
  return s.length >= 16 ? `${s.slice(0, 10)} ${s.slice(11, 16)}Z` : s;
};
/**
 * Whole CALENDAR days between two instants, measured date-to-date.
 *
 * Deliberately not a timestamp diff. The claim rendered from this is "N days before",
 * which a reader takes as calendar days — and diffing a first-seen TIMESTAMP against
 * midnight on an anchor DATE loses most of a day and rounds down. That shipped once:
 * 2026-07-19 19:20:06Z vs an IocDate of 2026-07-29 rendered "9 days" where every
 * other record of this case — the plan, the changelog, the release notes, the issue —
 * says TEN, which is what 07-19 to 07-29 is. The headline number of the whole feature
 * disagreed with itself on screen.
 */
const daysBetween = (aIso, bIso) => {
  const a = Date.parse(`${String(aIso).slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${String(bIso).slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86400000);
};

function Chip({ tone = 'neutral', children, title }) {
  const tones = {
    neutral: 'bg-gray-800 border-gray-700 text-gray-300',
    info: 'bg-sky-950/40 border-sky-800/60 text-sky-300',
    warn: 'bg-amber-950/40 border-amber-700/60 text-amber-300',
    bad: 'bg-red-950/40 border-red-700/60 text-red-300',
    good: 'bg-green-950/30 border-green-700/50 text-green-300',
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] whitespace-nowrap ${tones[tone] || tones.neutral}`}
    >
      {children}
    </span>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-gray-500 w-28 shrink-0">{label}</span>
      <span className="text-gray-300 min-w-0 flex-1">{children}</span>
    </div>
  );
}

// Entra sign-in error codes worth naming. Anything unlisted renders as the bare code
// rather than a guess - a wrong expansion of an error code is worse than none.
const ERR_LABELS = {
  '50173': 'grant revoked',
  '50076': 'MFA required',
  '50079': 'MFA enrolment required',
  '50126': 'invalid credentials',
  '50132': 'credentials invalidated',
  '50158': 'external security challenge',
  '53003': 'blocked by Conditional Access',
  '65001': 'consent required',
  '70000': 'invalid grant',
  '700082': 'refresh token expired',
};

export default function NonInteractiveEvidencePanel({
  row,                 // one entry from the endpoint's `ips[]`
  adjacency = null,    // adjacencyFor(ip, index)
  deviceIndex = {},    // deviceId(lower) -> endpoint `devices[]` entry
  caseDeviceNames = null, // Set of device names already present in the case results
  caseStartDate = '',
  iocDate = '',
  firstAttackerActivity = '',
  coverage = null,
  failureCoverage = null,
  hasMaliciousClassification = false,
  sessionIndex = null,      // sessionId -> { ips, maliciousIps, sensitiveOps }
  activityInWindow = null,  // (fromIso, toIso) -> { total, malicious } from the case events
}) {
  if (!row) return null;

  const devices = row.devices || [];
  const deviceIds = row.deviceIds || [];
  const tokenOnly = (row.withDevice || 0) === 0 && (row.events || 0) > 0;
  const mostlyTokenOnly = (row.events || 0) > 0 && (row.withDevice || 0) / row.events < 0.1;
  const scripted = scriptedClients(row.userAgents);
  const adjText = adjacencyBadgeText(adjacency);
  const appCounts = row.appCounts || [];
  const errorCodes = row.errorCodes || [];
  const failedTotal = errorCodes.reduce((a, e) => a + (e.count || 0), 0);

  // Sessions this IP's tokens belong to, cross-referenced against what the CASE already
  // knows about that session. This is the join the case cannot otherwise make: a session
  // is created by an interactive sign-in (audited, in the case) and then replayed
  // non-interactively (this feed) - each side holds half the story.
  const sessions = (row.sessionIds || []).map((sess) => ({
    ...sess,
    ctx: sessionIndex ? sessionIndex[sess.name] || sessionIndex[String(sess.name).toLowerCase()] : null,
  }));

  // Containment read. lastSuccess plus a revoked-grant code is the pipeline team's test:
  // after a credential revoke the REAL user re-authenticates and goes on succeeding,
  // while a stolen token can only keep failing. Stated as observation, never as verdict -
  // "no success since" is a fact; "contained" is the analyst's call.
  const revoked = row.sawRevokedGrant || errorCodes.some((e) => e.name === '50173');
  const staleAfterRevoke = revoked && failedTotal > 0 && row.lastSuccess && row.lastSeen
    && row.lastSuccess < row.lastSeen;

  const windowActivity = activityInWindow && row.firstSeen && row.lastSeen
    ? activityInWindow(row.firstSeen, row.lastSeen)
    : null;

  // Devices seen on this IP that appear NOWHERE in the case results. This is the
  // `DESKTOP-D61DA9` shape — a device with zero rows in the shared events table and no
  // `Add device` audit event, so every audit-driven and sign-in-driven view is blind to it.
  const newDevices = caseDeviceNames
    ? devices.filter((d) => d && !caseDeviceNames.has(String(d).toUpperCase()))
    : [];

  // Timeline callouts, most actionable first. Only meaningful for a row the analyst is
  // actually weighing, so the caller gates rendering of this panel.
  const callouts = [];
  if (row.firstSeen) {
    if (caseStartDate && fmtDate(row.firstSeen) < caseStartDate) {
      callouts.push({
        tone: 'bad',
        text: `Activity begins ${fmtDate(row.firstSeen)} — before this case's window starts (${caseStartDate}). Widen the date range and re-run.`,
      });
    }
    if (iocDate && fmtDate(row.firstSeen) < iocDate) {
      const d = daysBetween(row.firstSeen, iocDate);
      callouts.push({
        tone: 'warn',
        text: `Activity begins ${fmtDate(row.firstSeen)} — ${d} day${d === 1 ? '' : 's'} before this case's first known indicator (${iocDate}), the date the case was opened from.`,
      });
    }
    if (firstAttackerActivity && row.firstSeen < firstAttackerActivity) {
      const d = daysBetween(row.firstSeen, firstAttackerActivity);
      if (d && d > 0) {
        callouts.push({
          tone: 'warn',
          text: `Activity begins ${d} day${d === 1 ? '' : 's'} before the earliest attacker activity currently in this case.`,
        });
      }
    }
  }

  return (
    <div className="mt-2 rounded-lg border border-sky-900/40 bg-sky-950/10 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <GlobeAltIcon className="h-4 w-4 text-sky-400" />
        <span className="text-xs font-semibold text-sky-300">Non-interactive sign-in activity</span>
        <span className="text-[11px] text-gray-500">
          token refresh / SSO reuse / PRT replay — not shown in the case timeline
        </span>
      </div>

      {/* 1. DEVICES — the primary tell. */}
      <div className="space-y-1.5">
        <Row label="Devices">
          {devices.length === 0 ? (
            <span className="text-amber-300">
              No device claim on any record
              <span className="text-gray-500"> — a caution, not a verdict (see note below)</span>
            </span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {devices.map((d) => {
                const id = (deviceIds.find((x) => deviceIndex[x]?.name === d) || deviceIds[0] || '').toLowerCase();
                const meta = deviceIndex[id];
                const isNew = newDevices.includes(d);
                return (
                  <Chip
                    key={d}
                    tone={isNew ? 'bad' : 'info'}
                    title={meta
                      ? `${meta.trustType || 'trust unknown'}${meta.registeredUtc ? ` · registered ${fmtDate(meta.registeredUtc)}` : ''}${meta.osVersion ? ` · OS ${meta.osVersion}` : ''}`
                      : 'Not found in the Entra device registry snapshot'}
                  >
                    <ComputerDesktopIcon className="h-3 w-3" />
                    {d}
                    {isNew && <span className="text-[10px] uppercase tracking-wide">new to case</span>}
                  </Chip>
                );
              })}
            </div>
          )}
        </Row>
        {newDevices.length > 0 && (
          <p className="text-[11px] text-red-300 pl-[7.5rem]">
            {newDevices.length === 1 ? 'This device appears' : 'These devices appear'} nowhere else in
            this case — no sign-in rows and no directory-audit registration event.
          </p>
        )}
        {row.trustTypes?.length > 0 && (
          <Row label="Trust type">{row.trustTypes.join(', ')}</Row>
        )}
      </div>

      {/* 2. ADJACENCY — the strongest single reason to look at a row first. */}
      {adjText && (
        <div className="flex items-start gap-2 rounded border border-red-800/50 bg-red-950/20 px-2 py-1.5">
          <ExclamationTriangleIcon className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
          <div className="text-xs text-red-300 min-w-0">
            <span className="font-semibold">{adjText}</span>
            {adjacency?.prefixPeers?.length > 0 && (
              <span className="text-red-400/80"> — {adjacency.prefixPeers.join(', ')}</span>
            )}
            {adjacency?.maliciousInAsn > 0 && adjacency?.asn && (
              <span className="text-gray-400"> · same network AS{adjacency.asn}</span>
            )}
          </div>
        </div>
      )}

      {/* 3. VOLUME & OUTCOME. */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-4">
        <Stat label="Events" value={row.events} />
        <Stat label="Successful" value={row.successes} />
        <Stat label="With device" value={`${row.withDevice} / ${row.events}`} />
        <Stat label="Active days" value={row.activeDays} />
      </div>
      <Row label="Seen">
        <span className="font-mono text-[11px]">{fmtDateTime(row.firstSeen)}</span>
        <span className="text-gray-600"> → </span>
        <span className="font-mono text-[11px]">{fmtDateTime(row.lastSeen)}</span>
        <span className="text-gray-500 ml-1">(earliest observed, not necessarily the start)</span>
      </Row>

      {/* 3b. SESSION LINKAGE - the join the case cannot otherwise make. */}
      {sessions.length > 0 && (
        <div className="space-y-1.5 rounded border border-sky-900/40 bg-sky-950/20 px-2 py-1.5">
          <p className="text-[11px] font-semibold text-sky-300 uppercase tracking-wide">
            {sessions.length > 1 ? `Sessions (${sessions.length})` : 'Session'}
          </p>
          {sessions.map((sess) => (
            <div key={sess.name} className="text-xs">
              <span className="font-mono text-[11px] text-gray-300">{sess.name}</span>
              <span className="text-gray-500">
                {' · '}{sess.count} token{sess.count === 1 ? '' : 's'} from this IP
              </span>
              {sess.ctx ? (
                <div className="mt-0.5 pl-2 border-l border-sky-900/60 space-y-0.5">
                  <p className={sess.ctx.maliciousIps?.length > 0 ? 'text-red-300' : 'text-gray-400'}>
                    Same session as {sess.ctx.ips.length} IP{sess.ctx.ips.length === 1 ? '' : 's'} already
                    in this case
                    {sess.ctx.maliciousIps?.length > 0 && (
                      <>
                        {' — '}
                        <span className="font-semibold">
                          {sess.ctx.maliciousIps.length} classified malicious
                        </span>
                        {' ('}{sess.ctx.maliciousIps.slice(0, 4).join(', ')}{')'}
                      </>
                    )}
                  </p>
                  {sess.ctx.sensitiveOps?.length > 0 && (
                    <p className="text-red-300">
                      Actions recorded on this session:{' '}
                      <span className="font-medium">{sess.ctx.sensitiveOps.slice(0, 5).join(', ')}</span>
                    </p>
                  )}
                  <p className="text-[11px] text-gray-500">
                    A shared session is corroboration, not attribution: a stolen token keeps the
                    victim&apos;s own session id, so the same session legitimately carries their activity
                    too. Judge each event on the IP recorded against it.
                  </p>
                </div>
              ) : (
                <div className="mt-0.5 pl-2 border-l border-gray-800">
                  <p className="text-[11px] text-gray-500">
                    This session appears nowhere in the case&apos;s own events &mdash; token activity only.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 3c. CONTINUITY - what the case shows during this IP's token window. */}
      {windowActivity && (
        <p className="text-xs text-gray-400">
          During this IP&apos;s window the case itself shows{' '}
          <span className="text-gray-200">{windowActivity.total.toLocaleString()}</span> event
          {windowActivity.total === 1 ? '' : 's'}
          {windowActivity.malicious > 0 ? (
            <>
              {', '}
              <span className="text-red-300">{windowActivity.malicious.toLocaleString()}</span> from IPs
              classified malicious.
            </>
          ) : (
            <>
              {'.'}
              {/* Only say "none classified malicious" when the analyst has actually classified
                  something. With an empty malicious set that clause is true by construction and
                  reads as a finding nobody made - the risk flagged before this case was run. */}
              {hasMaliciousClassification && (
                <>
                  {' '}
                  <span className="text-amber-300">None came from an IP classified malicious</span>, so
                  the case reads as normal across this period while these tokens were being refreshed.
                </>
              )}
            </>
          )}
        </p>
      )}

      {/* 3d. OUTCOME / CONTAINMENT. */}
      {(failedTotal > 0 || revoked) && (
        <div className="space-y-1 rounded border border-gray-700/60 bg-gray-900/40 px-2 py-1.5">
          <p className="text-[11px] font-semibold text-gray-300 uppercase tracking-wide">Outcome</p>
          <div className="flex flex-wrap gap-1.5">
            {errorCodes.map((e) => (
              <Chip
                key={e.name}
                tone={e.name === '50173' ? 'warn' : 'neutral'}
                title={ERR_LABELS[e.name] || 'Entra sign-in error code'}
              >
                {e.name}{ERR_LABELS[e.name] ? ` · ${ERR_LABELS[e.name]}` : ''} &times;{e.count}
              </Chip>
            ))}
          </div>
          {row.lastSuccess && (
            <p className="text-xs text-gray-300">
              Last success{' '}
              <span className="font-mono text-[11px]">{fmtDateTime(row.lastSuccess)}</span>
              {staleAfterRevoke && (
                <span className="text-amber-200">
                  {' — then '}{failedTotal} failure{failedTotal === 1 ? '' : 's'} and no success since.
                </span>
              )}
            </p>
          )}
          {revoked && (
            <p className="text-[11px] text-gray-500">
              <span className="text-gray-400">50173 means the token grant was revoked.</span> After a
              credential reset the real user can re-authenticate and resume succeeding; a stolen token
              cannot. Compare this IP&apos;s last success against the user&apos;s known-good IPs before
              concluding either way.
            </p>
          )}
        </div>
      )}

      {/* 4. NETWORK. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {row.enriched ? (
          <>
            <Chip tone={row.networkType === 'Hosting' ? 'warn' : 'neutral'}>
              {row.provider || 'provider unknown'}
            </Chip>
            {row.networkType && (
              <Chip tone={row.networkType === 'Hosting' ? 'warn' : 'neutral'}>{row.networkType}</Chip>
            )}
          </>
        ) : (
          <Chip
            tone="warn"
            title="This IP has no row in the IP-enrichment table at all — it is invisible to any hunt keyed on provider, geography or risk score. That is an absence of data, not an absence of risk."
          >
            (unenriched)
          </Chip>
        )}
        {row.asn && row.asn !== '0' && <Chip tone="neutral">AS{row.asn}</Chip>}
        {(row.countries || []).map((c) => <Chip key={c} tone="neutral">{c}</Chip>)}
        {(row.tokenTypes || []).filter((t) => t && t !== 'none').map((t) => (
          <Chip key={t} tone="info" title="Token type presented">{t}</Chip>
        ))}
      </div>

      {/* 4b. WHAT THE TOKENS WERE FOR - resources, not actions. */}
      {appCounts.length > 0 && (
        <div className="space-y-1">
          <Row label="Resources">
            <div className="flex flex-wrap gap-1.5">
              {appCounts.slice(0, 10).map((a) => (
                <Chip key={a.name} tone="neutral">{a.name} &times;{a.count}</Chip>
              ))}
            </div>
          </Row>
          <p className="text-[11px] text-gray-500 pl-[7.5rem]">
            These are the resources tokens were obtained FOR &mdash; not actions taken. This feed records
            authentication only; whatever was actually done appears in the case timeline, under the IP
            recorded on that event.
          </p>
        </div>
      )}

      {/* 5. CAUTIONS — never verdicts. */}
      {(tokenOnly || mostlyTokenOnly || scripted.length > 0) && (
        <div className="space-y-1 rounded border border-amber-900/40 bg-amber-950/10 px-2 py-1.5">
          <p className="text-[11px] font-semibold text-amber-300 uppercase tracking-wide">Cautions</p>
          {tokenOnly && (
            <p className="text-xs text-amber-200">
              Token-only — no record from this IP carried a device claim.
            </p>
          )}
          {!tokenOnly && mostlyTokenOnly && (
            <p className="text-xs text-amber-200">
              Mostly token-only — {row.noDevice} of {row.events} records carried no device claim.
            </p>
          )}
          {scripted.length > 0 && (
            <p className="text-xs text-amber-200">
              Scripted client: <span className="font-mono">{scripted.join(', ')}</span>
              <span className="text-gray-400">
                {' '}— a caution only. Automation tooling is common on legitimate traffic; on this
                tenant the victim&apos;s own office IP carries <span className="font-mono">node-fetch</span>,
                and an estate-wide sweep for this pattern returned an incident responder&apos;s own tooling.
              </span>
            </p>
          )}
        </div>
      )}

      {/* 6. TIMELINE CONSEQUENCE — the wrong-start-date harm, said in words. */}
      {callouts.map((c) => (
        <div
          key={c.text}
          className={`flex items-start gap-2 rounded border px-2 py-1.5 ${
            c.tone === 'bad'
              ? 'border-red-800/50 bg-red-950/20 text-red-300'
              : 'border-amber-800/50 bg-amber-950/20 text-amber-300'
          }`}
        >
          <ClockIcon className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="text-xs">{c.text}</span>
        </div>
      ))}

      {/* 7. SCOPE FOOTER — what this panel cannot tell you. */}
      <div className="flex items-start gap-2 border-t border-gray-800 pt-2">
        <InformationCircleIcon className="h-3.5 w-3.5 text-gray-600 shrink-0 mt-0.5" />
        <div className="text-[11px] text-gray-500 space-y-1">
          {coverage?.startsAfterWindow && (
            <p className="text-amber-500/90">
              This feed holds no data for this tenant before {coverage.feedFirstDate} — the first{' '}
              {coverage.uncoveredDays} day{coverage.uncoveredDays === 1 ? '' : 's'} of the case window
              are not covered, so an earlier first-seen may exist and simply not be collected.
            </p>
          )}
          {failureCoverage && !failureCoverage.failuresComparable && (
            <p className="text-amber-500/90">
              {failureCoverage.measured && failureCoverage.daysWithFailures === 0
                ? 'No failed non-interactive sign-ins were collected for this tenant in this window, so failure counts here are unknown rather than zero — do not read a last-success time as a containment result.'
                : 'Failure rows are uneven across this window, so a low failure count may be a collection artefact rather than evidence that nothing was attempted and rejected.'}
            </p>
          )}
          <p>
            The device rule above does not apply to Exchange-sourced IPs, which carry no device detail
            and are not in this feed at all.
          </p>
          <p>
            Proxy-style AiTM (a look-alike host relaying the sign-in) does <span className="italic">not</span>{' '}
            appear in this feed — it bypasses Entra sign-in logs entirely. Absence here is not evidence
            against it.
          </p>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm text-gray-200 font-mono">{value ?? '—'}</div>
    </div>
  );
}
